import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, UserPlus, Loader2, KeyRound, Trash2, Copy } from "lucide-react";

const BLANK = { name: "", email: "", role: "viewer_management" };

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [roles, setRoles] = useState({});
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ ...BLANK });
  const [saving, setSaving] = useState(false);
  const [cred, setCred] = useState(null);

  const load = () => api.get("/admin/users").then((r) => { setUsers(r.data.users); setRoles(r.data.roles); }).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Enter the user's name");
    if (!form.email.trim()) return toast.error("Enter an email");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/users", form);
      setInviteOpen(false); setForm({ ...BLANK });
      setCred({ title: "Admin user created", email: data.email, password: data.temp_password, login_link: data.login_link });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create user"); }
    finally { setSaving(false); }
  };

  const changeRole = async (u, role) => {
    try { await api.put(`/admin/users/${u.id}`, { role }); toast.success(`${u.name} is now ${roles[role]}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update role"); }
  };
  const toggleActive = async (u) => {
    try { await api.put(`/admin/users/${u.id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const resetPw = async (u) => {
    try { const { data } = await api.post(`/admin/users/${u.id}/reset-password`); setCred({ title: "Password reset", email: data.email, password: data.temp_password, login_link: data.login_link }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not reset password"); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}? They will lose admin access immediately.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <div className="max-w-5xl" data-testid="admin-users-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight flex items-center gap-2"><Users className="w-7 h-7 text-olive" /> Admin Users</h1>
        <Button onClick={() => { setForm({ ...BLANK }); setInviteOpen(true); }} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="add-admin-user-btn"><UserPlus className="w-4 h-4 mr-1" /> Add User</Button>
      </div>
      <p className="text-sm text-stone-500 mb-6">Create internal staff logins and assign each a role. Permissions per role are managed under Roles &amp; Permissions.</p>

      <div className="bg-white rounded-2xl soft-shadow p-5">
        {!users ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-sand text-brown/70 text-xs uppercase tracking-wide">
                <tr><th className="text-left px-3 py-2.5">User</th><th className="text-left px-3 py-2.5">Role</th><th className="text-left px-3 py-2.5">Status</th><th className="text-right px-3 py-2.5">Actions</th></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-sage/15" data-testid={`admin-user-${u.id}`}>
                    <td className="px-3 py-3">
                      <p className="text-forest font-500">{u.name} {u.is_self && <span className="text-[10px] text-olive">(you)</span>}{u.is_primary && <span className="text-[10px] text-mustard ml-1">Primary</span>}</p>
                      <p className="text-xs text-brown/60">{u.email}</p>
                      {u.temp_password && <p className="text-[11px] text-earth mt-0.5">Temp password active — awaiting first login</p>}
                    </td>
                    <td className="px-3 py-3">
                      {u.is_primary ? <Badge className="bg-forest text-cream hover:bg-forest">Super Admin</Badge> : (
                        <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                          <SelectTrigger className="h-8 rounded-full w-[240px] bg-sand border-sage/40" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{Object.entries(roles).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <button onClick={() => !u.is_self && !u.is_primary && toggleActive(u)} disabled={u.is_self || u.is_primary} data-testid={`user-toggle-${u.id}`}>
                        <Badge className={u.active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-brown/10 text-brown/50 hover:bg-brown/10"}>{u.active ? "Active" : "Suspended"}</Badge>
                      </button>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="inline-flex gap-1.5">
                        <button onClick={() => resetPw(u)} title="Reset password" data-testid={`user-reset-${u.id}`} className="w-8 h-8 rounded-full border border-sage inline-flex items-center justify-center hover:bg-sage/10"><KeyRound className="w-4 h-4 text-olive" /></button>
                        {!u.is_self && !u.is_primary && <button onClick={() => remove(u)} title="Remove" data-testid={`user-remove-${u.id}`} className="w-8 h-8 rounded-full border border-terracotta/40 inline-flex items-center justify-center hover:bg-terracotta/10"><Trash2 className="w-4 h-4 text-terracotta" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-cream max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-forest">Add Admin User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm mb-1 block">Full Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl bg-white" data-testid="admin-invite-name" /></div>
            <div><Label className="text-sm mb-1 block">Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl bg-white" data-testid="admin-invite-email" /></div>
            <div><Label className="text-sm mb-1 block">Role *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-xl bg-white" data-testid="admin-invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(roles).filter(([k]) => k !== "super_admin").map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={create} disabled={saving} className="rounded-full bg-forest hover:bg-earth text-cream" data-testid="admin-invite-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Login"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cred} onOpenChange={(o) => !o && setCred(null)}>
        <DialogContent className="bg-cream max-w-md" data-testid="admin-cred-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-forest">{cred?.title}</DialogTitle></DialogHeader>
          {cred && (
            <div className="space-y-3">
              <p className="text-sm text-brown/70">Share these login details securely. The temporary password must be changed on first login.</p>
              {[["Login Email", cred.email], ["Temporary Password", cred.password], ["Admin Portal", cred.login_link]].map(([l, v]) => v && (
                <div key={l} className="bg-white rounded-xl p-3 flex items-center justify-between gap-2">
                  <div className="min-w-0"><p className="text-[10px] uppercase text-olive">{l}</p><p className="text-sm text-forest font-500 truncate">{v}</p></div>
                  <button onClick={() => copy(v)} className="shrink-0 text-olive hover:text-forest"><Copy className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter><Button onClick={() => setCred(null)} className="rounded-full bg-forest text-cream">Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
